import { type ReactNode, useEffect } from 'react';
import { MortarboardLineIcon, Slider } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useIsChannelEmpty } from 'features/channels/hooks/useIsChannelEmpty';
import type { ProgramsListFilter } from '../../types';
import { ProgramCard } from '../program-card';
import { usePrograms } from './hooks';

type P = {
  colSpan?: 10 | 7;
  controls: {
    hasTitleIcon?: boolean;
    seeAll?: { limit?: number; onClick?: () => void };
    title: ReactNode;
    titleLevel?: 2 | 4;
  };
  description?: ReactNode;
  emptyState?: ReactNode;
  getPathToProgramDetail: (id: string) => string;
  onEmptyState?: (isEmpty: boolean) => void;
  query?: { filter?: ProgramsListFilter };
};

const ProgramsSlider = ({
  colSpan,
  controls: { hasTitleIcon, seeAll, title, titleLevel },
  description,
  emptyState,
  getPathToProgramDetail,
  onEmptyState,
  query,
}: P) => {
  const {
    query: { data, fetchNextPage, hasNextPage, isFetching, isLoading },
  } = usePrograms({
    allowUnfeatured: Boolean(seeAll?.onClick),
    filter: query?.filter,
    pagination: { pageSize: 4 },
  });

  const programs = data?.pages.flatMap((programsPaginated) => programsPaginated.data);
  const programsTotal = data?.pages[0]?.total ?? 0;

  const isEmpty = useIsChannelEmpty(isLoading, programs);

  // TODO this hack is needed together with useEffect in default channels to set empty state correctly
  useEffect(() => {
    onEmptyState?.(isEmpty);
  }, [isEmpty, onEmptyState]);

  return (
    <Slider
      colSpan={colSpan}
      controls={{
        seeAll: { limit: seeAll?.limit, onClick: seeAll?.onClick, total: programsTotal },
        title,
        titleIcon: hasTitleIcon ? <MortarboardLineIcon /> : undefined,
        titleLevel,
      }}
      description={description}
      emptyState={emptyState}
      items={programs?.map((program) => ({
        children: <ProgramCard getPathToProgramDetail={getPathToProgramDetail} program={program} />,
        key: `program-${program.id}`,
      }))}
      queryData={{
        fetchNextPage,
        hasNextPage,
        isFetching,
        isLoading,
      }}
    />
  );
};

export default ProgramsSlider;
